import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Query } from "appwrite";
import { Eye } from "lucide-react";
import { tablesDB, account, DB_ID, ORDERS_TABLE } from "../../appwrite";
import "./SellerOrders.css";
import { useTitle } from "../../hooks/useTitle";

const PER_PAGE = 15;

const parseInfoArray = (arr) => {
  if (!Array.isArray(arr)) return {};
  const r = {};
  arr.forEach((item) => {
    if (typeof item === "string") {
      try { Object.assign(r, JSON.parse(item)); }
      catch { const i = item.indexOf(":"); if (i > -1) r[item.slice(0, i).trim()] = item.slice(i + 1).trim(); }
    }
  });
  return r;
};

const fmtDate = (v) => {
  if (!v) return "-";
  try {
    return new Date(v).toLocaleDateString("en-NG", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "-";
  }
};

const BADGE = {
  paid: "ord-badge--paid",
  unpaid: "ord-badge--unpaid",
  pending: "ord-badge--pending",
  fulfilled: "ord-badge--delivered",
  delivered: "ord-badge--delivered",
  shipping: "ord-badge--shipping",
  "out-for-delivery": "ord-badge--shipping",
  cancelled: "ord-badge--cancelled",
};
const badge = (v) => BADGE[(v || "").toLowerCase()] || "ord-badge--pending";

export default function SellerOrders() {
  useTitle("Orders - TrendBay Seller");
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const meta = document.querySelector("meta[name='theme-color']");
    const orig = meta?.getAttribute("content");
    if (meta) meta.setAttribute("content", "#080F0D");
    return () => {
      if (meta) meta.setAttribute("content", orig);
    };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const user = await account.get();
        const res = await tablesDB.listRows({
          databaseId: DB_ID,
          tableId: ORDERS_TABLE,
          queries: [Query.orderDesc("$createdAt"), Query.limit(500)],
        });
        const rows = (res.rows || []).map((r) => ({ id: r.$id, ...r }));
        const sellerRows = rows.filter((o) => {
          const cartItems = (o.cart || []).map((item) => {
            try {
              return JSON.parse(item);
            } catch {
              return item;
            }
          });
          return cartItems.some((item) => item.sellerId === user.$id);
        });
        setOrders(sellerRows);
        setFiltered(sellerRows);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const q = query.toLowerCase();
    setFiltered(
      orders.filter(
        (o) =>
          String(o.orderNumber || "").includes(q) ||
          (parseInfoArray(o.shippingInfo).fullName || "").toLowerCase().includes(q),
      ),
    );
    setPage(1);
  }, [query, orders]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const slice = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <div className="ord-page">
      <div className="ord-header">
        <div>
          <h1 className="ord-title">Orders</h1>
          <p className="ord-subtitle">{orders.length} orders total</p>
        </div>
      </div>

      <div className="ord-search-wrap">
        <input
          className="ord-search"
          placeholder="Search by order # or customer name…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div className="ord-table-wrap">
        {loading ? (
          <p className="ord-empty">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="ord-empty">No orders found.</p>
        ) : (
          <table className="ord-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Date</th>
                <th>Customer</th>
                <th>Payment</th>
                <th>Fulfillment</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {slice.map((order) => (
                <tr
                  key={order.id}
                  className="ord-row"
                  onClick={() => navigate(`/s/order-details/${order.id}`)}
                >
                  <td className="ord-order-num">
                    #{order.orderNumber || "N/A"}
                  </td>
                  <td>{fmtDate(order.$createdAt)}</td>
                  <td>{parseInfoArray(order.shippingInfo).fullName || "-"}</td>
                  <td>
                    <span className={`ord-badge ${badge(order.paymentStatus)}`}>
                      {order.paymentStatus || "-"}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`ord-badge ${badge(order.fulfillmentStatus)}`}
                    >
                      {order.fulfillmentStatus || "-"}
                    </span>
                  </td>
                  <td>
                    <button
                      className="ord-view-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/s/order-details/${order.id}`);
                      }}
                    >
                      <Eye size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Mobile cards */}
      <div className="ord-mobile-cards">
        {loading ? (
          <p className="ord-empty">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="ord-empty">No orders found.</p>
        ) : (
          slice.map((order) => (
            <div
              key={order.id}
              className="ord-mobile-card"
              onClick={() => navigate(`/s/order-details/${order.id}`)}
            >
              <div className="ord-mobile-card__top">
                <span className="ord-order-num">
                  #{order.orderNumber || "N/A"}
                </span>
                <span className={`ord-badge ${badge(order.paymentStatus)}`}>
                  {order.paymentStatus || "-"}
                </span>
              </div>
              <div className="ord-mobile-card__mid">
                <span>{parseInfoArray(order.shippingInfo).fullName || "-"}</span>
                <span>{fmtDate(order.$createdAt)}</span>
              </div>
              <div className="ord-mobile-card__bot">
                <span className={`ord-badge ${badge(order.fulfillmentStatus)}`}>
                  {order.fulfillmentStatus || "-"}
                </span>
                <Eye size={15} color="#00A855" />
              </div>
            </div>
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div className="ord-pagination">
          <button
            className="ord-page-btn"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            ← Prev
          </button>
          <span className="ord-page-info">
            {page} / {totalPages}
          </span>
          <button
            className="ord-page-btn"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
